import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { XRayAnchor, XRayOpenState } from './xrayTypes'
import { XRayOverlay } from './XRayOverlay'
import { XRayContext, type XRayContextValue } from './xrayContext'

export type { XRayContextValue }

export const XRayProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [openState, setOpenState] = useState<XRayOpenState | null>(null)

  const close = useCallback(() => {
    setOpenState(null)
  }, [])

  const open = useCallback(
    (anchor: XRayAnchor, triggeredFrom: { sliceId: string; page: string }) => {
      // SWAP: replaces existing openState cleanly
      setOpenState({ anchor, triggeredFrom })
    },
    [],
  )

  // Global scroll listener: any wheel/scroll closes X-ray
  useEffect(() => {
    if (!openState) return

    const handleWheel = () => {
      close()
    }

    window.addEventListener('wheel', handleWheel, { capture: true, passive: true })
    return () => {
      window.removeEventListener('wheel', handleWheel, { capture: true })
    }
  }, [close, openState])

  const value = useMemo<XRayContextValue>(
    () => ({ openState, open, close }),
    [close, open, openState],
  )

  return (
    <XRayContext.Provider value={value}>
      {children}
      {openState ? <XRayOverlay state={openState} onClose={close} /> : null}
    </XRayContext.Provider>
  )
}
